import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

function Settings({ onLogout }) {
  const [settings, setSettings] = useState({
    sendgrid_configured: false,
    smtp_configured: false,
    sender_email: '',
    smtp_host: 'mail.smtp2go.com',
    smtp_port: 587,
    stripe_enabled: false,
    stripe_price_id: '',
    support_amount: 5.0,
    bunny_configured: false,
    bunny_storage_zone: '',
    bunny_storage_region: 'ny',
    bunny_pull_zone_url: '',
    shippo_configured: false,
    notification_email: '',
    default_og_image: '',
    collect_sales_tax: false,
    tax_shipping: false
  });
  const [formData, setFormData] = useState({
    sendgrid_api_key: '',
    sender_email: '',
    admin_password: '',
    stripe_enabled: false,
    stripe_price_id: '',
    support_amount: 5.0,
    bunny_storage_api_key: '',
    shippo_api_key: '',
    bunny_storage_zone: '',
    bunny_storage_region: 'ny',
    bunny_pull_zone_url: '',
    smtp_host: 'mail.smtp2go.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    notification_email: '',
    default_og_image: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/settings');
      setSettings(response.data);
      setFormData({
        sendgrid_api_key: '',
        sender_email: response.data.sender_email || '',
        admin_password: '',
        stripe_enabled: response.data.stripe_enabled || false,
        stripe_price_id: response.data.stripe_price_id || '',
        support_amount: response.data.support_amount || 5.0,
        bunny_storage_api_key: '',
        bunny_storage_zone: response.data.bunny_storage_zone || '',
        bunny_storage_region: response.data.bunny_storage_region || 'ny',
        bunny_pull_zone_url: response.data.bunny_pull_zone_url || '',
        shippo_api_key: '',
        smtp_host: response.data.smtp_host || 'mail.smtp2go.com',
        smtp_port: response.data.smtp_port || 587,
        smtp_username: response.data.smtp_username || '',
        smtp_password: '',
        notification_email: response.data.notification_email || '',
        default_og_image: response.data.default_og_image || '',
        collect_sales_tax: response.data.collect_sales_tax || false,
        tax_shipping: response.data.tax_shipping || false
      });
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Only send non-empty fields
      const updateData = {};
      if (formData.sendgrid_api_key) updateData.sendgrid_api_key = formData.sendgrid_api_key;
      if (formData.sender_email) updateData.sender_email = formData.sender_email;
      if (formData.admin_password) updateData.admin_password = formData.admin_password;
      updateData.stripe_enabled = formData.stripe_enabled;
      if (formData.stripe_price_id) updateData.stripe_price_id = formData.stripe_price_id;
      updateData.support_amount = formData.support_amount;
      if (formData.bunny_storage_api_key) updateData.bunny_storage_api_key = formData.bunny_storage_api_key;
      if (formData.bunny_storage_zone) updateData.bunny_storage_zone = formData.bunny_storage_zone;
      if (formData.bunny_storage_region) updateData.bunny_storage_region = formData.bunny_storage_region;
      if (formData.bunny_pull_zone_url) updateData.bunny_pull_zone_url = formData.bunny_pull_zone_url;
      if (formData.shippo_api_key) updateData.shippo_api_key = formData.shippo_api_key;
      if (formData.smtp_host) updateData.smtp_host = formData.smtp_host;
      if (formData.smtp_port) updateData.smtp_port = formData.smtp_port;
      if (formData.smtp_username) updateData.smtp_username = formData.smtp_username;
      if (formData.smtp_password) updateData.smtp_password = formData.smtp_password;
      if (formData.notification_email) updateData.notification_email = formData.notification_email;
      if (formData.default_og_image) updateData.default_og_image = formData.default_og_image;
      updateData.collect_sales_tax = formData.collect_sales_tax;
      updateData.tax_shipping = formData.tax_shipping;

      await axios.post('/settings', updateData);
      toast.success('Settings saved successfully');
      
      // Clear sensitive fields
      setFormData({
        ...formData,
        sendgrid_api_key: '',
        admin_password: '',
        shippo_api_key: ''
      });
      
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout onLogout={onLogout} currentPage="settings">
        <div className="loading-spinner"></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onLogout={onLogout} currentPage="settings">
      <div data-testid="settings-page">
        <div className="page-header">
          <h1>Settings</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card">
            <h2>Email Configuration (SMTP2GO)</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              {settings.smtp_configured ? (
                <span style={{ color: '#28a745' }}>✓ SMTP2GO is configured</span>
              ) : (
                <span style={{ color: '#dc3545' }}>SMTP2GO not configured - add credentials below</span>
              )}
            </p>

            <div className="form-group">
              <label htmlFor="smtp_username">SMTP Username</label>
              <input
                type="text"
                id="smtp_username"
                data-testid="smtp-username-input"
                className="form-input"
                value={formData.smtp_username}
                onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                placeholder="your-smtp2go-username"
              />
              <small style={{ color: '#666' }}>
                Your SMTP2GO username from{' '}
                <a href="https://www.smtp2go.com/" target="_blank" rel="noopener noreferrer">
                  SMTP2GO Dashboard
                </a>
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="smtp_password">SMTP Password / API Key</label>
              <input
                type="password"
                id="smtp_password"
                data-testid="smtp-password-input"
                className="form-input"
                value={formData.smtp_password}
                onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                placeholder="Your SMTP2GO password or API key"
              />
              <small style={{ color: '#666' }}>
                Use your SMTP2GO password or API key
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="sender_email">Sender Email</label>
              <input
                type="email"
                id="sender_email"
                data-testid="sender-email-input"
                className="form-input"
                value={formData.sender_email}
                onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
                placeholder="newsletter@yourdomain.com"
              />
              <small style={{ color: '#666' }}>
                Must be verified in SMTP2GO (Sending → Verified Senders)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="notification_email">Order Notification Email</label>
              <input
                type="email"
                id="notification_email"
                className="form-input"
                multiple
                value={formData.notification_email}
                onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                placeholder="roye@beachbumlife.net, wife@example.com"
              />
              <small style={{ color: '#666' }}>
                Where to send new order alerts — separate multiple addresses with a comma. Requires SMTP2GO to be configured above.
              </small>
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="smtp_host">SMTP Host</label>
                <input
                  type="text"
                  id="smtp_host"
                  data-testid="smtp-host-input"
                  className="form-input"
                  value={formData.smtp_host}
                  onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                  placeholder="mail.smtp2go.com"
                />
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="smtp_port">SMTP Port</label>
                <input
                  type="number"
                  id="smtp_port"
                  data-testid="smtp-port-input"
                  className="form-input"
                  value={formData.smtp_port}
                  onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                  placeholder="587"
                />
                <small style={{ color: '#666' }}>
                  Common ports: 587 (recommended), 2525, 8025
                </small>
              </div>
            </div>
          </div>

          <div className="card">
            <h2>Supporter Subscriptions (Stripe)</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Enable optional monthly subscriptions for supporters. All content remains free.
            </p>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  data-testid="stripe-enabled-checkbox"
                  checked={formData.stripe_enabled}
                  onChange={(e) => setFormData({ ...formData, stripe_enabled: e.target.checked })}
                />
                Enable Supporter Subscriptions
              </label>
            </div>

            {formData.stripe_enabled && (
              <div className="form-group">
                <label htmlFor="support_amount">Monthly Support Amount (USD)</label>
                <input
                  type="number"
                  id="support_amount"
                  data-testid="support-amount-input"
                  className="form-input"
                  value={formData.support_amount}
                  onChange={(e) => setFormData({ ...formData, support_amount: parseFloat(e.target.value) })}
                  min="1"
                  step="0.01"
                />
                <small style={{ color: '#666' }}>
                  Supporters will pay this amount monthly (using Emergent's Stripe integration)
                </small>
              </div>
            )}
          </div>

          <div className="card">
            <h2>Sales Tax</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Uses Stripe Tax. Tax is added on top of prices at checkout, only for buyers in states registered
              in your Stripe dashboard (Settings → Tax), at the rate for their county.{' '}
              <strong>Only turn this on after Stripe Tax is set up there</strong> — otherwise Stripe rejects every checkout.
              Turning it off puts checkout back to no tax.
            </p>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={formData.collect_sales_tax}
                  onChange={(e) => setFormData({ ...formData, collect_sales_tax: e.target.checked })}
                />
                Collect sales tax at checkout
              </label>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={formData.tax_shipping}
                  disabled={!formData.collect_sales_tax}
                  onChange={(e) => setFormData({ ...formData, tax_shipping: e.target.checked })}
                />
                Also charge tax on shipping
              </label>
            </div>
          </div>

          <div className="card">
            <h2>Image Storage (Bunny.net CDN)</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              {settings.bunny_configured ? (
                <span style={{ color: '#28a745' }}>✓ Bunny.net is configured</span>
              ) : (
                <span style={{ color: '#dc3545' }}>Bunny.net not configured - add credentials below</span>
              )}
            </p>

            <div className="form-group">
              <label htmlFor="bunny_storage_api_key">Bunny Storage API Key (Password)</label>
              <input
                type="password"
                id="bunny_storage_api_key"
                data-testid="bunny-api-key-input"
                className="form-input"
                value={formData.bunny_storage_api_key}
                onChange={(e) => setFormData({ ...formData, bunny_storage_api_key: e.target.value })}
                placeholder="Your storage zone password"
              />
              <small style={{ color: '#666' }}>
                Find in Bunny.net Dashboard → Storage → FTP & API Access (use full password, not read-only)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="bunny_storage_zone">Storage Zone Name</label>
              <input
                type="text"
                id="bunny_storage_zone"
                data-testid="bunny-zone-input"
                className="form-input"
                value={formData.bunny_storage_zone}
                onChange={(e) => setFormData({ ...formData, bunny_storage_zone: e.target.value })}
                placeholder="my-storage-zone"
              />
              <small style={{ color: '#666' }}>
                Your storage zone name from Bunny.net
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="bunny_storage_region">Storage Region</label>
              <select
                id="bunny_storage_region"
                data-testid="bunny-region-select"
                className="form-input"
                value={formData.bunny_storage_region}
                onChange={(e) => setFormData({ ...formData, bunny_storage_region: e.target.value })}
              >
                <option value="ny">New York</option>
                <option value="de">Frankfurt (Default)</option>
                <option value="uk">London</option>
                <option value="la">Los Angeles</option>
                <option value="sg">Singapore</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="bunny_pull_zone_url">Pull Zone URL</label>
              <input
                type="url"
                id="bunny_pull_zone_url"
                data-testid="bunny-pullzone-input"
                className="form-input"
                value={formData.bunny_pull_zone_url}
                onChange={(e) => setFormData({ ...formData, bunny_pull_zone_url: e.target.value })}
                placeholder="https://my-zone.b-cdn.net"
              />
              <small style={{ color: '#666' }}>
                Your Pull Zone URL (e.g., https://yourzone.b-cdn.net)
              </small>
            </div>
          </div>

          <div className="card">
            <h2>Shipping (Shippo)</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              {settings.shippo_configured ? (
                <span style={{ color: '#28a745' }}>✓ Shippo is configured</span>
              ) : (
                <span style={{ color: '#dc3545' }}>Shippo not configured - add your API token below</span>
              )}
            </p>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              When configured, every paid order is pushed to your Shippo account automatically as soon as it's placed — it shows up in your Shippo Orders queue ready for you to buy a label whenever you're ready to ship. This never buys a label or charges anything on its own.
            </p>

            <div className="form-group">
              <label htmlFor="shippo_api_key">Shippo API Token</label>
              <input
                type="password"
                id="shippo_api_key"
                data-testid="shippo-api-key-input"
                className="form-input"
                value={formData.shippo_api_key}
                onChange={(e) => setFormData({ ...formData, shippo_api_key: e.target.value })}
                placeholder="shippo_live_... or shippo_test_..."
              />
              <small style={{ color: '#666' }}>
                Find it under Settings → API in your{' '}
                <a href="https://apps.goshippo.com/" target="_blank" rel="noopener noreferrer">
                  Shippo Dashboard
                </a>
                . Use a test token first to confirm orders show up before switching to your live token.
              </small>
            </div>
          </div>

          <div className="card">
            <h2>Social Sharing</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Used when a post has no image of its own, so links shared on Facebook/Twitter still show a preview.
            </p>
            <div className="form-group">
              <label htmlFor="default_og_image">Default Share Image URL</label>
              <input
                type="url"
                id="default_og_image"
                className="form-input"
                value={formData.default_og_image}
                onChange={(e) => setFormData({ ...formData, default_og_image: e.target.value })}
                placeholder="https://my-zone.b-cdn.net/default-share-image.jpg"
              />
              <small style={{ color: '#666' }}>
                Must be a direct HTTPS image URL, ideally 1200x630px
              </small>
            </div>
          </div>

          <div className="card">
            <h2>Admin Password</h2>
            <div className="form-group">
              <label htmlFor="admin_password">New Password (leave blank to keep current)</label>
              <input
                type="password"
                id="admin_password"
                data-testid="admin-password-input"
                className="form-input"
                value={formData.admin_password}
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                placeholder="Enter new password"
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" data-testid="save-settings-btn" disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

export default Settings;